/* ===================================================================
   SharkFoto Free Tools — honest "unconvertible target" explainer engine.
   When a converter pair X-to-Y has a TARGET Y the backend cannot WRITE
   (raw / fonts / proprietary editor formats / not-yet-built / legacy
   read-only / …), we must NOT render a dead converter page and must NOT
   404 — we render a truthful landing that explains why, and points to the
   conversions that actually work. Drives RawTargetExplainer.vue.

   Single source of truth for writability: format-capabilities.js
   (auto-generated from sharkfoto-fastapi/format-capabilities.csv).
   Self-contained on purpose (no import from seo-data.js) to avoid a cycle.
   =================================================================== */
import { FORMATS, F } from './formats-data.js';
import { capOf, isUnwritableTarget } from './format-capabilities.js';

const BASE = 'https://sharkfoto.com/free-tools';
const OG = 'https://cdn.sharkfoto.com/sharkfoto_og.png';

export { isUnwritableTarget };

// ---- small helpers -------------------------------------------------
const capFirst = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
function humanJoin(arr) {
  if (!arr.length) return '';
  if (arr.length === 1) return arr[0];
  return arr.slice(0, -1).join(', ') + ' or ' + arr[arr.length - 1];
}
const catOf = (ext) => (capOf(ext) && capOf(ext).cat) || 'raster';

// 格式知识块(源 + 目标),与 converter 页同深度:全名/开发者/年份/扩展名/MIME/透明度 + 科普 + 如何打开。
// 数据取自 formats-data 的 FORMATS(FULL/DEV/YEAR/MIME/ALPHA/WHAT/OPEN 列都在)。别名归一(JPEG->JPG 等)。
const FMT_ALIAS = { JPEG: 'JPG', JPE: 'JPG', JFIF: 'JPG', TIF: 'TIFF' };
function fmtRow(ext) {
  const e = FMT_ALIAS[ext] || ext;
  return FORMATS.find((r) => String(r[F.EXT]).toUpperCase() === e);
}
// 兜底:少数非可写目标不在 FORMATS 注册表里(否则目标卡缺,页面偏薄)。{full,dev,year,mime,alpha,what,open}
const EXTRA_FMT = {
  PUB: { full: 'Microsoft Publisher Document', dev: 'Microsoft', year: '1991', mime: 'application/x-mspublisher', alpha: 'No',
    what: 'PUB is Microsoft Publisher’s page-layout format for flyers, brochures, newsletters and cards. It stores editable text boxes and graphics, and is native to Publisher.',
    open: 'PUB opens in Microsoft Publisher; LibreOffice can import it, and exporting to PDF makes it viewable anywhere.' },
  VSD: { full: 'Visio Drawing (legacy)', dev: 'Microsoft', year: '1992', mime: 'application/vnd.visio', alpha: 'No',
    what: 'VSD is the legacy binary format of Microsoft Visio (pre-2013), used for flowcharts, network diagrams and org charts. It was superseded by the XML-based VSDX.',
    open: 'VSD opens in Microsoft Visio and compatible diagram tools; export to VSDX, PDF or an image to share.' },
  PWP: { full: 'Seattle FilmWorks Image', dev: 'Seattle FilmWorks', year: '1990s', mime: 'image/x-pwp', alpha: 'No',
    what: 'PWP is a proprietary 1990s photo format from the Seattle FilmWorks / PhotoWorks mail-order photo lab. It is rarely seen today and largely obsolete.',
    open: 'PWP needs the old Seattle FilmWorks / PhotoWorks software, or a converter, to open it as a standard image.' },
  SRW: { full: 'Samsung RAW', dev: 'Samsung', year: '2010', mime: 'image/x-samsung-srw', alpha: 'No',
    what: 'SRW is Samsung’s camera RAW format, storing the unprocessed sensor data captured by Samsung cameras for maximum editing latitude.',
    open: 'SRW opens in Adobe Lightroom, Photoshop / Camera Raw, Capture One and Samsung’s own software.' },
  RAW: { full: 'Camera Raw', dev: 'Camera manufacturers', year: '—', mime: 'image/x-raw', alpha: 'No',
    what: 'A raw file is the unprocessed data straight from a camera’s sensor, keeping maximum detail and dynamic range before it is developed into a JPG or PNG. “RAW” isn’t one format — each maker has its own (CR2, NEF, ARW…).',
    open: 'RAW files open in Lightroom, Photoshop / Camera Raw, Capture One and each camera maker’s software.' },
};
function fmtBlock(ext) {
  const r = fmtRow(ext);
  const facts = (full, dev, year, mime, alpha) => [
    ['Full name', full], ['Developed by', dev], ['Released', year],
    ['Extension', '.' + String(ext).toLowerCase()], ['MIME type', mime], ['Transparency', alpha],
  ];
  // 形状对齐 SeoFormatDetails 的 { title, body, facts, openTitle, open }(该组件每块渲染成"是什么"+"怎么打开"两个手风琴项)。
  if (r) {
    return { title: `What is a ${ext} file?`, body: r[F.WHAT],
      facts: facts(r[F.FULL], r[F.DEV], r[F.YEAR], r[F.MIME], r[F.ALPHA]),
      openTitle: `How to open a ${ext} file`, open: r[F.OPEN] };
  }
  const x = EXTRA_FMT[ext];
  if (x) {
    return { title: `What is a ${ext} file?`, body: x.what,
      facts: facts(x.full, x.dev, x.year, x.mime, x.alpha),
      openTitle: `How to open a ${ext} file`, open: x.open };
  }
  return null;
}

// pair URL segment, mirroring seo-data.pairCategory (kept local to avoid a cycle)
function pairSeg(from, to) {
  const a = catOf(from), b = catOf(to);
  const Fu = String(from).toUpperCase(), Tu = String(to).toUpperCase();
  if (a === 'video' || b === 'video' || Fu === 'GIF' || Tu === 'GIF') return 'video';
  if (a === 'audio' || b === 'audio') return 'audio';
  return 'image/converter';
}

// recommended REAL (writable) targets per category, for "do this instead" cards
const ALT_TARGETS = {
  raster: ['PNG', 'JPG', 'TIFF', 'WEBP', 'PDF'],
  raw: ['TIFF', 'PNG', 'JPG', 'WEBP'],
  icon: ['PNG', 'ICO', 'ICNS'],
  animated: ['GIF', 'APNG', 'WEBP', 'MP4'],
  vector: ['SVG', 'PDF', 'EPS', 'DXF'],
  print: ['PDF', 'TIFF', 'PNG'],
  document: ['PDF', 'PNG', 'JPG'],
  plist: ['JSON', 'XML'],
  audio: ['MP3', 'WAV', 'AAC', 'FLAC'],
  video: ['MP4', 'WEBM', 'MOV', 'GIF'],
};
const ALT_DESC = {
  PNG: 'Lossless with transparency — perfect for graphics, logos and screenshots',
  JPG: 'A small, universal photo format that opens everywhere',
  TIFF: 'Lossless and uncompressed — ideal for editing, archiving and print',
  WEBP: 'A modern format with small files at high quality for the web',
  PDF: 'A universal document you can share, print and open anywhere',
  SVG: 'An editable vector that stays crisp at any size',
  EPS: 'A print-ready vector format for professional design software',
  DXF: 'An editable CAD/vector outline for AutoCAD, CNC and laser cutting',
  GIF: 'A widely supported animated image',
  APNG: 'A higher-quality animated PNG with full color and transparency',
  MP4: 'The universal video that plays on any device',
  WEBM: 'An open, web-optimized video format',
  MOV: 'Apple QuickTime video',
  MP3: 'The universal compressed audio format',
  WAV: 'Uncompressed, lossless audio',
  AAC: 'Efficient modern audio used by streaming and Apple',
  FLAC: 'Lossless audio at about half the size of WAV',
  ICO: 'The Windows icon and favicon format',
  ICNS: 'The macOS app-icon format',
};

function altPool(fromU, cat) {
  return (ALT_TARGETS[cat] || ALT_TARGETS.raster).filter((t) => t !== fromU).slice(0, 3);
}
function altCards(fromU, pool) {
  const fromSlug = fromU.toLowerCase();
  return pool.map((t) => ({
    title: `Convert ${fromU} to ${t}`,
    desc: (ALT_DESC[t] || `A widely supported format`) + '.',
    to: `/free-tools/${pairSeg(fromU, t)}/${fromSlug}-to-${t.toLowerCase()}/`,
    cta: `${fromU} → ${t}`,
  }));
}
function oneAltCard(fromU, t, desc) {
  return {
    title: `Convert ${fromU} to ${t}`,
    desc: desc || (ALT_DESC[t] || 'A widely supported format') + '.',
    to: `/free-tools/${pairSeg(fromU, t)}/${fromU.toLowerCase()}-to-${t.toLowerCase()}/`,
    cta: `${fromU} → ${t}`,
  };
}

// explainer 页的"相关工具"推荐云(RelatedToolCards + 底部标签云用),按源格式类目给相关的热门工具。
// [label, slug] —— slug 走 ftHref 解析(a-to-b 配对经 getToolSEO 归正 / 已知单工具 slug)。别用会落空的压缩器裸 slug。
const CLOUD_BY_CAT = {
  raster: [['Image Converter', 'image-converter'], ['PNG to JPG', 'png-to-jpg'], ['HEIC to JPG', 'heic-to-jpg'], ['WEBP to PNG', 'webp-to-png'], ['Resize Image', 'resize-image'], ['Crop Image', 'crop-image'], ['PNG to WEBP', 'png-to-webp'], ['Image Compressor', 'image-compressor']],
  raw: [['ARW to DNG', 'arw-to-dng'], ['HEIC to JPG', 'heic-to-jpg'], ['TIFF to DNG', 'tiff-to-dng'], ['DNG to JPG', 'dng-to-jpg'], ['Image Converter', 'image-converter'], ['Resize Image', 'resize-image']],
  vector: [['SVG Converter', 'svg-converter'], ['PNG to SVG', 'png-to-svg'], ['SVG to PNG', 'svg-to-png'], ['EPS to PNG', 'eps-to-png'], ['Image Converter', 'image-converter'], ['PDF to PNG', 'pdf-to-png']],
  icon: [['PNG to ICO', 'png-to-ico'], ['ICO to PNG', 'ico-to-png'], ['PNG to ICNS', 'png-to-icns'], ['SVG to ICNS', 'svg-to-icns'], ['Image Converter', 'image-converter'], ['Resize Image', 'resize-image']],
  audio: [['MP4 to MP3', 'mp4-to-mp3'], ['MP3 to WAV', 'mp3-to-wav'], ['MP3 to OGG', 'mp3-to-ogg'], ['WAV to MP3', 'wav-to-mp3'], ['Video to MP3', 'video-to-mp3'], ['M4A to MP3', 'm4a-to-mp3']],
  video: [['MOV to MP4', 'mov-to-mp4'], ['Video to MP3', 'video-to-mp3'], ['MP4 to GIF', 'mp4-to-gif'], ['Video to GIF', 'video-to-gif'], ['WEBM to MP4', 'webm-to-mp4'], ['GIF to MP4', 'gif-to-mp4']],
  animated: [['Video to GIF', 'video-to-gif'], ['MP4 to GIF', 'mp4-to-gif'], ['GIF to MP4', 'gif-to-mp4'], ['APNG to GIF', 'apng-to-gif'], ['Image to GIF', 'image-to-gif'], ['GIF Compressor', 'gif-compressor']],
  plist: [['Image Converter', 'image-converter'], ['PNG to JPG', 'png-to-jpg'], ['Resize Image', 'resize-image'], ['Crop Image', 'crop-image'], ['PNG to WEBP', 'png-to-webp'], ['Image Compressor', 'image-compressor']],
};
function explainerCloud(fromU) {
  return CLOUD_BY_CAT[catOf(fromU)] || CLOUD_BY_CAT.raster;
}

// ---- knowledge maps ------------------------------------------------
const RAW_MAKERS = {
  CR2: 'Canon', CR3: 'Canon', CRW: 'Canon', NEF: 'Nikon', NRW: 'Nikon',
  ARW: 'Sony', SR2: 'Sony', SRF: 'Sony', RAF: 'Fujifilm', RW2: 'Panasonic',
  ORF: 'Olympus', PEF: 'Pentax', DCR: 'Kodak', KDC: 'Kodak', K25: 'Kodak',
  '3FR': 'Hasselblad', FFF: 'Hasselblad', IIQ: 'Phase One', MEF: 'Mamiya',
  MOS: 'Leaf', MRW: 'Minolta', RWL: 'Leica', SRW: 'Samsung', X3F: 'Sigma', ERF: 'Epson',
};
const TARGET_WHAT = {
  CUR: 'CUR is the Windows cursor format — like an ICO but with a hotspot, used to make custom mouse pointers.',
  DDS: 'DDS (DirectDraw Surface) is a GPU texture format used in games and 3D engines, supporting compressed mipmapped textures.',
  EXR: 'EXR (OpenEXR) is a high-dynamic-range format from Industrial Light & Magic, storing extreme color depth for VFX and 3D rendering.',
  JPS: 'JPS is a stereoscopic JPEG that stores a side-by-side 3D image pair.',
  XBM: 'XBM (X BitMap) is a simple monochrome bitmap stored as C source, historically used for X11 icons.',
  XPM: 'XPM (X PixMap) is a text-based color bitmap format used for X11 application icons.',
  EMF: 'EMF (Enhanced Metafile) is a Windows vector format for scalable graphics and clip art.',
  SVGZ: 'SVGZ is simply a gzip-compressed SVG — identical vector data in a smaller file.',
  WMF: 'WMF (Windows Metafile) is a legacy Windows vector format for clip art and simple graphics.',
  CMYK: 'CMYK refers to the four-ink print color model; a CMYK file holds artwork prepared for professional printing rather than for screens.',
  PDFA: 'PDF/A is an ISO-standardized version of PDF built for long-term archiving, with fonts and color embedded.',
  DJVU: 'DjVu is a compact format for scanned documents and books, often smaller than PDF at the same quality.',
  AI: "AI is Adobe Illustrator's native vector format for logos and illustrations that scale to any size.",
  HPGL: 'HPGL/PLT stores vector plotting commands for pen plotters, vinyl cutters and CNC machines.',
  DCM: 'DCM (DICOM) is the medical-imaging standard used by CT, MRI and X-ray equipment, bundling the image with patient and study metadata.',
};
const LEGACY_WHAT = {
  JXR: 'JXR (JPEG XR) is a Microsoft high-dynamic-range format with little support outside Windows.',
  MNG: 'MNG is a little-used animated cousin of PNG that never gained browser support.',
  SID: 'SID (MrSID) is a proprietary wavelet format for huge geospatial images, needing a vendor SDK to write.',
  FLIF: 'FLIF was an experimental lossless format whose development stopped in favor of newer codecs.',
  FPX: 'FPX (FlashPix) is a 1990s multi-resolution format that has effectively disappeared.',
  PCD: 'PCD (Kodak Photo CD) is a 1990s format from scanned film, read-only in modern tools.',
  MAC: 'MAC (MacPaint) is a 1980s one-bit Macintosh bitmap format.',
  PIX: 'PIX is an Alias/Wavefront raster format from early 3D software.',
  RLA: 'RLA is a Wavefront raster format used in early 3D and VFX pipelines.',
  RLE: 'RLE (Utah RLE) is an old run-length-encoded raster format.',
  SCT: 'SCT (Scitex CT) is a legacy high-end prepress format.',
  SFW: 'SFW (Seattle FilmWorks) is a proprietary 1990s photo-lab format.',
  CUT: 'CUT (Dr. Halo) is a 1980s DOS paint format.',
  TIM: 'TIM is the PlayStation 1 texture image format.',
  RAS: 'RAS (Sun Raster) is a legacy Unix raster format.',
  SUN: 'SUN (Sun Raster) is a legacy Unix raster format.',
  VIFF: 'VIFF (Khoros) is a legacy scientific-imaging raster format.',
  PWP: 'PWP (Seattle FilmWorks) is a proprietary 1990s photo format.',
};
const PROP_APP = { CDR: 'CorelDRAW', VSD: 'Microsoft Visio', VSDX: 'Microsoft Visio', WPG: 'WordPerfect / Corel', PUB: 'Microsoft Publisher' };
const FONT_DESC = { TTF: 'a TrueType font', PFA: 'a PostScript Type 1 font', PFB: 'a PostScript Type 1 font' };
const PIXEL_DESC = { BGR: 'blue-green-red samples', GRAY: 'grayscale samples', MONO: 'one-bit black-and-white samples', YUV: 'luma and chroma samples', UYVY: 'packed luma and chroma samples' };
const MEDIA_WHAT = { AMR: 'an old mobile voice-recording codec', RA: 'RealAudio, an early streaming format', RM: 'RealMedia, an early streaming video format' };

// camera-raw landscape grid (answers "what are the actual RAW formats?")
const RAW_GRID = FORMATS
  .filter((r) => r[F.TYPE] === 'raw' && String(r[F.EXT]).toUpperCase() !== 'XMP')
  .map((r) => ({ ext: r[F.EXT], full: r[F.FULL], dev: r[F.DEV], href: '/format/' + String(r[F.EXT]).toLowerCase() + '/' }));

// ---- kind selection ------------------------------------------------
function explainerKind(fromU, toU, cap) {
  const e = toU.toLowerCase();
  if (e === 'raw') return 'raw-generic';
  if (e === 'dng') return 'dng';
  if (e === 'xmp') return 'metadata'; // XMP sits in the 'raw' category but is metadata, not a raw image
  if (cap.cat === 'raw') return 'raw-proprietary';
  if (['ttf', 'pfa', 'pfb'].includes(e)) return 'font';
  if (['cdr', 'vsd', 'vsdx', 'wpg', 'pub'].includes(e)) return 'proprietary';
  if (e === 'xcf') return 'editor';
  if (['bgr', 'gray', 'mono', 'yuv', 'uyvy'].includes(e)) return 'raw-pixel';
  if (e === 'thm') return 'sidecar';
  if (e === 'xmp') return 'metadata';
  if (e === 'mid') return 'midi';
  if (e === 'swf') return 'flash';
  if (e === 'brf') return 'braille';
  if (e === 'pcl') return 'printer-language';
  if (['epdf', 'pdfa'].includes(e)) return 'pdf-variant';
  if (cap.cat === 'audio' || cap.cat === 'video') return 'media-readonly';
  if (cap.rec === 'build-converter' || cap.rec === 'coming-soon') return 'coming-soon';
  return 'legacy-readonly';
}

// ---- per-kind copy -------------------------------------------------
function explainerCopy(fromU, toU, kind, cap) {
  const fromSlug = fromU.toLowerCase();
  const srcCat = catOf(fromU);

  switch (kind) {
    case 'raw-generic': {
      const pool = ['TIFF', 'PNG'].filter((t) => t !== fromU);
      const alts = pool.map((t) => oneAltCard(fromU, t,
        t === 'TIFF'
          ? 'Lossless and uncompressed — the best choice for editing or archiving at full quality.'
          : 'Lossless too, and opens everywhere — keeps every pixel with no quality loss.'));
      alts.push({ title: 'Already have RAW photos?', desc: 'Convert your CR2, NEF, ARW, DNG and other camera RAW files to JPG, PNG or TIFF.', to: '/free-tools/image/converter/', cta: 'Open the converter' });
      return {
        verdictTone: 'no',
        verdict: `You can't convert ${fromU} to RAW — and no tool genuinely can. Here's why.`,
        lead: `Looking to turn a ${fromU} into a RAW file? Here's the honest answer — and what actually works instead.`,
        whyTitle: `Why ${fromU} to RAW isn't possible`,
        reasons: [
          { h: 'RAW is sensor data, not a picture', p: `A RAW file is the unprocessed data captured straight off a camera's image sensor the instant you press the shutter. A ${fromU} is an already-processed, finished image — the original sensor information is long gone and can't be recreated.` },
          { h: "It's tied to a specific camera", p: 'Each RAW format — Canon CR2, Nikon NEF, Sony ARW, Fuji RAF and dozens more — is proprietary to one camera maker and model. There is no single universal "RAW" file to convert into.' },
          { h: "The detail can't be rebuilt", p: `Converting to RAW can't bring back the extra dynamic range, color depth or highlight recovery that real RAW holds — that data was never in your ${fromU}. You'd only get a larger file with none of the benefits.` },
        ],
        targetFormats: RAW_GRID,
        targetFormatsTitle: 'So what are the actual RAW formats?',
        targetFormatsSub: '"RAW" isn\'t a single format — it\'s dozens of camera-maker-specific ones. A real RAW file is always one of these, straight from the camera:',
        alternatives: alts,
        faq: [
          [`Can I convert ${fromU} to RAW?`, `No. RAW is the unprocessed data a camera's sensor records at the moment of capture — it can't be generated from an already-processed ${fromU}, or from any finished image. Software converts from RAW, never into it.`],
          [`What's the difference between RAW and ${fromU}?`, `${fromU} is a finished, ready-to-view image. A RAW file holds the original sensor data with far more dynamic range and editing latitude — but it only ever comes straight out of a camera, not from a converter.`],
          ['Is DNG the same as RAW?', `DNG (Adobe Digital Negative) is an open RAW container. You can convert a camera's proprietary RAW — CR2, NEF, ARW and so on — into DNG, but you still can't turn a regular ${fromU} into RAW.`],
          ['How do I open a RAW file?', "RAW files open in Adobe Lightroom, Photoshop and Camera Raw, Capture One, and each maker's own software — Canon DPP, Nikon NX Studio, Sony Imaging Edge and more."],
          [`Can I convert RAW to ${fromU} or JPG instead?`, 'Yes — that direction works. Use our converter to turn CR2, NEF, ARW, DNG and other RAW files into JPG, PNG or TIFF.'],
          ['Will converting to RAW improve my image quality?', `No. You can't recover detail, dynamic range or color depth that was never captured. Your ${fromU} already contains all the data it has.`],
        ],
      };
    }

    case 'raw-proprietary': {
      const maker = RAW_MAKERS[toU] || 'the camera maker';
      const alts = altCards(fromU, altPool(fromU, 'raw'));
      alts.push({ title: `Already have ${maker} RAW?`, desc: `Convert your ${toU} and other camera RAW files to JPG, PNG or TIFF.`, to: '/free-tools/image/converter/', cta: 'Open the converter' });
      return {
        verdictTone: 'no',
        verdict: `You can't convert ${fromU} to ${toU}. ${toU} is a camera's own RAW format — it only ever comes out of the camera.`,
        lead: `Looking to turn a ${fromU} into a ${toU} file? Here's the honest answer — and what actually works instead.`,
        whyTitle: `Why ${fromU} to ${toU} isn't possible`,
        reasons: [
          { h: `${toU} is ${maker}'s in-camera RAW`, p: `${toU} is the raw sensor data a ${maker} camera writes at the moment of capture. It's produced by the camera's own hardware and firmware — software can read it, but nothing can manufacture a genuine ${toU} from a finished ${fromU}.` },
          { h: `A ${fromU} has already been processed`, p: `Your ${fromU} is a developed, final image. The extra sensor information a ${toU} carries — wider dynamic range, full color depth, untouched highlights — was discarded when the photo was first saved, and can't be put back.` },
          { h: 'It is locked to one camera maker', p: `${toU} is proprietary to ${maker}. Even other RAW formats (NEF, ARW, RAF…) aren't interchangeable with it — there is no universal "save as RAW" that produces a real ${toU}.` },
        ],
        targetFormats: RAW_GRID,
        targetFormatsTitle: 'The RAW formats — and where they come from',
        targetFormatsSub: 'Every real RAW file is one of these camera-maker-specific formats, written by the camera itself:',
        alternatives: alts,
        faq: [
          [`Can any tool convert ${fromU} to ${toU}?`, `No. ${toU} stores the raw signal a ${maker} sensor recorded at capture. A ${fromU} is a finished image with that data already baked in and discarded — there's nothing for a converter to reconstruct.`],
          [`Why would I want ${toU} anyway?`, `RAW formats like ${toU} give maximum editing latitude — but only when they come straight from the camera. Converting a ${fromU} to ${toU} would just wrap a finished image in a RAW container, with none of the benefits.`],
          ['Is DNG different?', `DNG is an open RAW container. You can convert a camera's own RAW (including ${toU}) into DNG, but you still can't turn a regular ${fromU} into ${toU} or any real RAW.`],
          [`What can I convert my ${fromU} to instead?`, `${fromU} converts cleanly to PNG, JPG, TIFF and WEBP — free, no sign-up, no watermark.`],
        ],
      };
    }

    case 'dng': {
      const fromIsRaw = srcCat === 'raw';
      const pool = altPool(fromU, 'raw');
      const alts = altCards(fromU, pool);
      if (!fromIsRaw) alts.push({ title: 'Have camera RAW files?', desc: 'Convert CR2, NEF, ARW and other camera RAW to JPG, PNG or TIFF.', to: '/free-tools/image/converter/', cta: 'Open the converter' });
      return {
        verdictTone: fromIsRaw ? 'soon' : 'no',
        verdict: fromIsRaw
          ? `${fromU} to DNG is a real conversion — it just isn't switched on at SharkFoto yet. Here's the status and what works today.`
          : `You can't create a genuine DNG from a finished ${fromU} — but DNG conversion from camera RAW is real. Here's the full picture.`,
        lead: `Wondering about ${fromU} to DNG? Here's exactly what works, what doesn't, and why.`,
        whyTitle: 'About converting to DNG',
        reasons: fromIsRaw
          ? [
              { h: `${fromU} to DNG is a true RAW-to-RAW conversion`, p: `${fromU} is camera RAW, and DNG (Adobe Digital Negative) is an open, standardized RAW container — so converting ${fromU} to DNG preserves the original sensor data while making it future-proof and editor-friendly. It's exactly what Adobe's DNG Converter does.` },
              { h: "It's coming to SharkFoto", p: `We're adding RAW-to-DNG conversion to the pipeline. It isn't live here yet — but it's a genuine, lossless conversion, not a workaround.` },
            ]
          : [
              { h: `DNG stores sensor data your ${fromU} no longer has`, p: `DNG is a RAW container built to hold the unprocessed data straight off a camera sensor. A ${fromU} is an already-developed image — wrapping it in a DNG can't recreate the dynamic range or color depth it never kept.` },
              { h: 'DNG conversion is real — from camera RAW', p: `Where DNG shines is converting a camera's own RAW (CR2, NEF, ARW…) into the open DNG format for archiving. That's a true conversion; turning a finished ${fromU} into RAW is not.` },
            ],
        targetFormats: RAW_GRID,
        targetFormatsTitle: 'DNG is an open container for these camera RAW formats',
        targetFormatsSub: 'DNG standardizes the proprietary RAW that cameras produce — these are the formats it replaces or wraps:',
        alternatives: alts,
        faq: [
          [`Can I convert ${fromU} to DNG?`, fromIsRaw
            ? `Yes in principle — ${fromU} is camera RAW and DNG is an open RAW container, so it's a lossless conversion. SharkFoto is adding it; meanwhile you can convert ${fromU} to JPG, PNG or TIFF.`
            : `Not meaningfully. DNG holds raw sensor data, which a finished ${fromU} doesn't contain. DNG conversion makes sense from a camera's own RAW files, not from a processed ${fromU}.`],
          ['What is DNG?', 'DNG (Adobe Digital Negative) is an open, royalty-free RAW format. It stores unprocessed sensor data in a standardized container any modern editor can open — ideal for archiving photos long-term.'],
          ['Is DNG better than CR2 / NEF / ARW?', "DNG isn't higher quality — it's more universal and future-proof. Many photographers convert their proprietary camera RAW to DNG so files stay openable for decades."],
          [`What can I convert my ${fromU} to today?`, `${fromU} converts cleanly to JPG, PNG, TIFF and WEBP right now — free and unlimited.`],
        ],
      };
    }

    case 'coming-soon': {
      const targetWhat = TARGET_WHAT[toU] || `${toU} is a ${cap.cat} format.`;
      const altCat = ['vector', 'icon'].includes(cap.cat) ? cap.cat : srcCat;
      const pool = altPool(fromU, altCat);
      const list = humanJoin(pool);
      return {
        verdictTone: 'soon',
        verdict: `${fromU} to ${toU} isn't available on SharkFoto yet — it's a real conversion we're working on. Here's what works today.`,
        lead: `Want to convert ${fromU} to ${toU}? It's on our roadmap. Here's what ${toU} is, and the conversions you can run right now.`,
        whyTitle: `About converting ${fromU} to ${toU}`,
        reasons: [
          { h: `What ${toU} is`, p: targetWhat },
          { h: `${toU} support is coming`, p: `Converting ${fromU} to ${toU} is technically possible and on our list — it just isn't switched on here yet. We add and test new output formats one at a time before release.` },
        ],
        alternatives: altCards(fromU, pool),
        faq: [
          [`Can I convert ${fromU} to ${toU} now?`, `Not on SharkFoto yet — ${toU} output is on our roadmap. In the meantime, ${fromU} converts to ${list} for free.`],
          [`What is ${toU}?`, targetWhat],
          [`Will you add ${fromU} to ${toU}?`, "It's planned. We're rolling out new output formats gradually and testing each before it goes live."],
          [`What can I convert ${fromU} to today?`, `${fromU} converts cleanly to ${list} — free, no sign-up, no watermark.`],
        ],
      };
    }

    case 'font': {
      const fontDesc = FONT_DESC[toU] || 'a font';
      const alts = [
        oneAltCard(fromU, 'SVG', 'Trace your image to editable vector outlines — the usual first step before importing into font or design software.'),
        oneAltCard(fromU, 'PNG', 'Keep it as an image in a universal, lossless format.'),
        oneAltCard(fromU, 'PDF', 'Save it as a shareable, printable document.'),
      ];
      return {
        verdictTone: 'no',
        verdict: `You can't convert a ${fromU} to ${toU} — ${fontDesc} isn't an image, and an image isn't a typeface.`,
        lead: `Trying to turn a ${fromU} into a ${toU} font? Here's why that isn't a conversion — and what to do instead.`,
        whyTitle: `Why ${fromU} to ${toU} isn't possible`,
        reasons: [
          { h: `${toU} is a font, not a picture`, p: `${toU} (${fontDesc}) describes the outlines of letters, numbers and symbols — a whole typeface. A ${fromU} is a single flat image of pixels. There's no meaningful way to turn one picture into a complete alphabet.` },
          { h: 'Fonts are built, not converted', p: 'Making a font means drawing or digitizing each glyph and setting metrics and kerning in font software like FontForge, Glyphs or FontLab. It isn’t an image conversion.' },
        ],
        alternatives: alts,
        faq: [
          [`Can any tool convert ${fromU} to ${toU}?`, `No. A ${fromU} is one image; ${toU} is a font containing every glyph of a typeface. To build a real font you draw the glyphs in font software — there's no image-to-font conversion.`],
          ['I have a logo image — how do I make it a font?', 'You’d first vectorize it (image → SVG), then import the glyphs into font software like FontForge or Glyphs and set the metrics. SharkFoto can do the first step, to SVG.'],
          [`What can I convert my ${fromU} to instead?`, `${fromU} converts to SVG, PNG, JPG, PDF and more — free and instant.`],
        ],
      };
    }

    case 'proprietary': {
      const app = PROP_APP[toU] || 'its design app';
      const pool = altPool(fromU, 'vector');
      const list = humanJoin(pool);
      const readable = !!(capOf(toU) && capOf(toU).readable);
      return {
        verdictTone: 'no',
        verdict: `You can't convert a ${fromU} to ${toU} — ${toU} is ${app}'s own format, and only ${app} writes it.`,
        lead: `Need a ${toU} from a ${fromU}? Here's why that isn't a direct conversion — and the editable formats that get you there.`,
        whyTitle: `Why ${fromU} to ${toU} isn't possible here`,
        reasons: [
          { h: `${toU} is proprietary to ${app}`, p: `${toU} is the native file format of ${app}. Its internal structure is closed, so the reliable way to create a ${toU} is to open or import your artwork in ${app} and save it there.` },
          { h: `A ${fromU} is a flat image`, p: `Even after importing, a ${fromU} is pixels — not the editable shapes, text and layers a ${toU} is designed to hold. So a direct ${fromU} → ${toU} conversion wouldn't give you a truly editable document anyway.` },
        ],
        alternatives: altCards(fromU, pool),
        faq: [
          [`Is there a free way to make a ${toU}?`, `Not by converting an image. ${toU} files are created in ${app} (or a compatible editor). Free tools can give you a vector — ${list} — that you then import into ${app}.`],
          [`Can SharkFoto open my existing ${toU}?`, readable
            ? `Yes — we can read ${toU} as a source, so you can convert a ${toU} you already have into PNG, JPG, SVG or PDF. We just can't create a new ${toU} from an image.`
            : `Not currently. ${toU} is best opened in ${app}.`],
          [`What should I convert my ${fromU} to instead?`, `For an editable, widely supported result, convert ${fromU} to ${list}.`],
        ],
      };
    }

    case 'editor': {
      const alts = [
        oneAltCard(fromU, 'PSD', 'The layered editor format that Photoshop, GIMP, Photopea and Affinity can all open.'),
        oneAltCard(fromU, 'PNG'),
        oneAltCard(fromU, 'TIFF'),
      ];
      return {
        verdictTone: 'no',
        verdict: `You can't convert a ${fromU} to XCF here — XCF is GIMP's own working format, written by GIMP itself.`,
        lead: `Want an XCF from your ${fromU}? Here's why it needs GIMP — and the layered format that works everywhere.`,
        whyTitle: `Why ${fromU} to XCF isn't possible here`,
        reasons: [
          { h: "XCF is GIMP's native format", p: `XCF (eXperimental Computing Facility) stores GIMP's full working state — layers, masks, paths and channels. It's the GIMP equivalent of Photoshop's PSD, and GIMP is what creates it.` },
          { h: `A ${fromU} has no layers to keep`, p: `A ${fromU} is a single flattened image. Saving it as XCF wouldn't add the layered, editable structure XCF exists for — so for most needs a standard format is the better target.` },
        ],
        alternatives: alts,
        faq: [
          [`Can any tool convert ${fromU} to XCF?`, `Not a real one. XCF is GIMP-specific; the dependable way to get an .xcf is to open your image in GIMP (free and open-source) and save it. SharkFoto can read XCF as a source, but doesn't write it.`],
          ['I want layers — what format should I use?', `Convert your ${fromU} to PSD. PSD is the layered format the widest range of editors — Photoshop, GIMP, Photopea, Affinity — can open.`],
          [`What can I convert my ${fromU} to instead?`, `${fromU} converts to PSD, PNG, JPG, TIFF and more — free and instant.`],
        ],
      };
    }

    case 'raw-pixel': {
      const px = PIXEL_DESC[toU] || 'raw pixel samples';
      const pool = altPool(fromU, srcCat);
      const list = humanJoin(pool);
      return {
        verdictTone: 'no',
        verdict: `${toU} isn't a normal image format — it's a headerless raw pixel buffer, so there's no meaningful "convert ${fromU} to ${toU}".`,
        lead: `Looking at ${fromU} to ${toU}? Here's why ${toU} isn't a standard "save as" target — and what to use.`,
        whyTitle: `Why ${fromU} to ${toU} isn't a standard conversion`,
        reasons: [
          { h: `${toU} has no header`, p: `A ${toU} file is just a stream of raw pixel values (${px}) with no width, height or color information stored inside. Anything opening it has to be told the exact dimensions and layout separately — so it isn't a portable image you can simply convert to.` },
          { h: 'Use a real container instead', p: `For an image you can actually open and share, pick a standard format that stores its own dimensions and color — ${list}.` },
        ],
        alternatives: altCards(fromU, pool),
        faq: [
          [`Why can't I save as ${toU}?`, `${toU} stores only raw pixel bytes with no header, so it's used inside processing pipelines, not as a shareable image. A standard format like PNG or TIFF keeps its own size and color info.`],
          [`What can I convert my ${fromU} to instead?`, `Convert ${fromU} to ${list} — free and instant.`],
        ],
      };
    }

    case 'sidecar': {
      const alts = [oneAltCard(fromU, 'JPG'), oneAltCard(fromU, 'PNG'), oneAltCard(fromU, 'WEBP')];
      return {
        verdictTone: 'no',
        verdict: `There's no real "convert ${fromU} to THM" — a THM file is simply a JPEG thumbnail a camera writes alongside a photo or video.`,
        lead: `Curious about ${fromU} to THM? Here's what THM actually is — and why there's nothing to convert.`,
        whyTitle: 'About THM files',
        reasons: [
          { h: 'THM is a JPEG with a different extension', p: `A .thm file is a small JPEG thumbnail cameras save next to a photo or video so devices can preview it quickly. Its contents are identical to a JPG — only the extension differs.` },
          { h: 'Nothing needs converting to it', p: `Because THM is created automatically by the camera, there's no use case for turning a ${fromU} into one. If you have a THM, just rename or convert it to .jpg to view it.` },
        ],
        alternatives: alts,
        faq: [
          ['How do I open a THM file?', "Rename it to .jpg, or convert it — it's a JPEG, so any image viewer will then open it."],
          [`Can I convert ${fromU} to THM?`, `There's no point: THM is just a camera's JPEG thumbnail. If you need a thumbnail-style image, convert ${fromU} to JPG.`],
          [`What can I convert my ${fromU} to instead?`, `${fromU} converts to JPG, PNG, WEBP, PDF and more — free and instant.`],
        ],
      };
    }

    case 'metadata': {
      const pool = altPool(fromU, srcCat);
      const list = humanJoin(pool);
      return {
        verdictTone: 'no',
        verdict: `You can't convert a ${fromU} to XMP — XMP is a text metadata sidecar, not an image format.`,
        lead: `Wondering about ${fromU} to XMP? Here's why it isn't a conversion — and what you might actually want.`,
        whyTitle: `Why ${fromU} to XMP isn't a conversion`,
        reasons: [
          { h: 'XMP holds settings, not pixels', p: `An XMP file is an Adobe sidecar that records edits, ratings, keywords and develop settings for a photo. It contains no image data, so there's nothing to convert a ${fromU} into.` },
          { h: 'It travels with a photo, not instead of it', p: 'Lightroom, Bridge and Camera Raw read an XMP next to the original (often a RAW) to re-apply edits. It only makes sense alongside that photo.' },
        ],
        alternatives: altCards(fromU, pool),
        faq: [
          ['What is an XMP file?', "XMP (Extensible Metadata Platform) is an Adobe sidecar storing a photo's edits and metadata as text — read by Lightroom, Bridge and Camera Raw."],
          [`Can I turn a ${fromU} into XMP?`, `No. XMP isn't an image; it stores settings for one. There's no image-to-metadata conversion.`],
          [`What can I convert my ${fromU} to instead?`, `${fromU} converts to ${list} and more — free and instant.`],
        ],
      };
    }

    case 'media-readonly': {
      const word = cap.cat === 'audio' ? 'audio' : 'video';
      const what = MEDIA_WHAT[toU] || `a legacy ${word} format`;
      const pool = altPool(fromU, cap.cat);
      const list = humanJoin(pool);
      return {
        verdictTone: 'no',
        verdict: `We don't convert to ${toU} — it's a legacy ${word} format that's effectively read-only. Here's what works.`,
        lead: `Looking for ${fromU} to ${toU}? Here's why ${toU} isn't an output option — and the current formats to use.`,
        whyTitle: `Why ${fromU} to ${toU} isn't offered`,
        reasons: [
          { h: `${toU} is a legacy ${word} format`, p: `${toU} is ${what}. It can be decoded for playback, but there's little reason to create new ${toU} files, so modern toolchains don't ship an encoder for it.` },
          { h: `Pick a current ${word} format`, p: `For a file that plays everywhere, convert to ${list} instead.` },
        ],
        alternatives: altCards(fromU, pool),
        faq: [
          [`Can I convert ${fromU} to ${toU}?`, `Not here. ${toU} is a legacy ${word} format with no modern encoder; ${fromU} converts to ${list} instead.`],
          [`What can I convert ${fromU} to?`, `${fromU} converts to ${list} — free and unlimited.`],
        ],
      };
    }

    case 'pdf-variant': {
      const isPdfa = toU === 'PDFA';
      const alts = [oneAltCard(fromU, 'PDF'), ...altCards(fromU, altPool(fromU, srcCat)).slice(0, 2)];
      return {
        verdictTone: isPdfa ? 'soon' : 'no',
        verdict: isPdfa
          ? `${fromU} to PDF/A isn't live yet — but a standard PDF works today and is what most uses need.`
          : `There's rarely a need to convert ${fromU} to EPDF — a standard PDF does the same job and opens everywhere.`,
        lead: `Thinking about ${fromU} to ${toU}? Here's what it is — and why a standard PDF is usually the answer.`,
        whyTitle: `About ${toU}`,
        reasons: isPdfa
          ? [
              { h: 'PDF/A is PDF built for archiving', p: 'PDF/A is an ISO-standardized PDF with fonts and color embedded so it renders identically decades from now. It is the format archives and courts require.' },
              { h: 'Coming to SharkFoto', p: `PDF/A output is on our roadmap. Until it's live, convert your ${fromU} to a standard PDF — PDF/A compliance can be applied later in archival tools.` },
            ]
          : [
              { h: 'EPDF is an encapsulated PDF', p: 'EPDF is a PDF meant to be embedded inside another document. For sharing, printing or viewing, a normal PDF is identical in practice and universally supported.' },
              { h: 'Just make a PDF', p: `Convert your ${fromU} to PDF — it opens in every browser and reader, and can be placed into other documents anyway.` },
            ],
        alternatives: alts,
        faq: [
          [`Should I use ${toU} or PDF?`, `For almost everything, use PDF — it's universal. ${toU} is a niche variant; convert ${fromU} to PDF and you're set.`],
          [`What can I convert my ${fromU} to?`, `${fromU} converts to PDF, PNG, JPG and more — free and instant.`],
        ],
      };
    }

    case 'printer-language': {
      const alts = [oneAltCard(fromU, 'PDF'), ...altCards(fromU, altPool(fromU, srcCat)).slice(0, 2)];
      return {
        verdictTone: 'no',
        verdict: `${fromU} to PCL isn't a normal conversion — PCL is a printer control language, not a shareable image or document format.`,
        lead: `Looking at ${fromU} to PCL? Here's why PCL isn't a file you'd save — and what to use to print or share.`,
        whyTitle: 'About PCL',
        reasons: [
          { h: 'PCL is for talking to printers', p: `PCL (Printer Command Language) is a stream of instructions a printer interprets to lay ink on a page. It's generated by a printer driver at print time, not something you save an image as to share.` },
          { h: 'Use PDF or an image instead', p: `To send a ${fromU} to print or share it, convert to PDF (or PNG/JPG) and print from there — your printer driver produces the PCL automatically.` },
        ],
        alternatives: alts,
        faq: [
          [`Can I convert ${fromU} to PCL?`, `Not as a file you'd use — PCL is produced by the printer driver when you print. Convert ${fromU} to PDF and print it; the driver handles the rest.`],
          [`What can I convert my ${fromU} to instead?`, `${fromU} converts to PDF, PNG, JPG and more — free and instant.`],
        ],
      };
    }

    case 'braille': {
      const alts = [oneAltCard(fromU, 'PDF'), oneAltCard(fromU, 'PNG'), oneAltCard(fromU, 'JPG')];
      return {
        verdictTone: 'no',
        verdict: `You can't convert a ${fromU} image to BRF — BRF is a text-based Braille format, produced from words, not pictures.`,
        lead: `Need a BRF from a ${fromU}? Here's why an image can't become BRF — and where to start.`,
        whyTitle: 'About BRF files',
        reasons: [
          { h: 'BRF is translated from text', p: 'A BRF (Braille Ready Format) file encodes braille cells generated from written text by translation software like Duxbury or liblouis. It carries no pictorial image data.' },
          { h: 'Start from text, not an image', p: `If your ${fromU} contains text, you'd first extract that text (OCR), then run it through braille-translation software to produce a BRF.` },
        ],
        alternatives: alts,
        faq: [
          [`Can any tool convert ${fromU} to BRF?`, 'Not directly. BRF comes from text via braille-translation software. Extract the text from your image first, then translate it.'],
          [`What can I convert my ${fromU} to instead?`, `${fromU} converts to PDF, PNG, JPG and more — free and instant.`],
        ],
      };
    }

    case 'midi': {
      const pool = altPool(fromU, srcCat);
      const list = humanJoin(pool);
      return {
        verdictTone: 'no',
        verdict: `You can't convert a ${fromU} to MIDI — MIDI stores musical performance instructions, not images or recorded sound.`,
        lead: `Wondering about ${fromU} to MIDI? Here's why they're unrelated — and what you can do.`,
        whyTitle: `Why ${fromU} to MIDI isn't possible`,
        reasons: [
          { h: 'MIDI is note data, not media', p: `A MIDI file is a list of events — which note, how hard, how long — for instruments to play. It contains no recorded audio and no image, so there's nothing in a ${fromU} to translate into it.` },
          { h: 'MIDI is composed or performed', p: 'MIDI is created in music software (a DAW) or by playing a MIDI instrument — not by converting a file.' },
        ],
        alternatives: altCards(fromU, pool),
        faq: [
          [`Can I convert ${fromU} to MIDI?`, `No. MIDI is note-and-timing data for instruments; a ${fromU} has none of that. There's no meaningful conversion between them.`],
          [`What can I convert my ${fromU} to instead?`, `${fromU} converts to ${list} — free and instant.`],
        ],
      };
    }

    case 'flash': {
      const pool = altPool(fromU, 'animated');
      const list = humanJoin(pool);
      return {
        verdictTone: 'no',
        verdict: `Converting ${fromU} to SWF isn't offered — SWF is the retired Adobe Flash format, and browsers dropped support for it in 2020.`,
        lead: `Looking at ${fromU} to SWF? Here's why SWF is a dead end — and the modern formats to use.`,
        whyTitle: 'About SWF (Flash) files',
        reasons: [
          { h: 'Flash (SWF) is end-of-life', p: 'Adobe discontinued Flash at the end of 2020 and every major browser removed support. New SWF files can’t play on the modern web, so creating them no longer makes sense.' },
          { h: 'Use a modern format', p: 'For animation, use an animated GIF, APNG or an MP4/WEBM video; for static graphics, use PNG or SVG.' },
        ],
        alternatives: altCards(fromU, pool),
        faq: [
          ['Can I still use SWF files?', 'Generally no — browsers removed Flash support in 2020. Convert your content to GIF, MP4 or WEBM instead.'],
          [`What can I convert my ${fromU} to instead?`, `${fromU} converts to ${list} — free and instant.`],
        ],
      };
    }

    default: { // legacy-readonly
      const what = LEGACY_WHAT[toU] || `${toU} is an older ${cap.cat} format.`;
      const pool = altPool(fromU, srcCat);
      const list = humanJoin(pool);
      return {
        verdictTone: 'no',
        verdict: `We don't create ${toU} files — it's a legacy format that's effectively read-only. Here's what to convert to instead.`,
        lead: `Looking for ${fromU} to ${toU}? Here's why ${toU} isn't an output option — and the modern formats to use.`,
        whyTitle: `Why ${fromU} to ${toU} isn't offered`,
        reasons: [
          { h: `${toU} is legacy and read-only here`, p: `${what} It can usually be opened and converted from, but it's long obsolete, so there's no modern encoder to create new ${toU} files.` },
          { h: 'Convert to a current format', p: `For a file that opens everywhere, convert your ${fromU} to ${list} instead.` },
        ],
        alternatives: altCards(fromU, pool),
        faq: [
          [`Can I convert ${fromU} to ${toU}?`, `Not here — ${toU} is a legacy format with no modern encoder. We can often read ${toU} as a source, but for output use a current format like ${list}.`],
          [`What can I convert my ${fromU} to instead?`, `${fromU} converts to ${list} — free, no sign-up, no watermark.`],
        ],
      };
    }
  }
}

// ---- unreadable-SOURCE explainer ----------------------------------
// The TARGET is fine, but the SOURCE format can't be decoded by ANY engine
// (readable=false in the capability table) — e.g. ITHMB, the Apple iPod/iPhone
// thumbnail-cache blob. Posting it to the backend always 422s, so we render an
// honest landing instead of a dead converter. Same payload shape as
// explainerSEO so RawTargetExplainer.vue renders it unchanged.
const UNREADABLE_SRC_WHAT = {
  ITHMB: "ITHMB is Apple's private iPod/iPhone thumbnail-cache format. It isn't a standard image file — it's an internal database of many tiny preview images packed together with no public specification, so no image library or converter can decode it.",
};
export function unreadableSourceSEO(fromU, toU, slug) {
  const srcCat = catOf(fromU);
  const what = UNREADABLE_SRC_WHAT[fromU] ||
    `${fromU} is a proprietary/undocumented format that no image library or converter can decode, so it can't be read as a source file.`;
  // Point users at conversions that DO work for the category they're likely in.
  // Drop the requested target too — telling them "do ITHMB→JPG instead" right after
  // "ITHMB→JPG is impossible" would contradict itself.
  const pool = altPool(fromU, srcCat === 'raw' ? 'raster' : srcCat).filter((t) => t !== toU).slice(0, 3);
  const list = humanJoin(pool);
  const copy = {
    verdictTone: 'no',
    verdict: `We can't convert ${fromU} to ${toU} — an ${fromU} file can't be opened by any converter. Here's why, and what actually works.`,
    lead: `Trying to turn a ${fromU} into ${toU}? Here's the honest answer — ${fromU} isn't a readable image format — and the steps that get you a real picture.`,
    whyTitle: `Why ${fromU} to ${toU} isn't possible`,
    reasons: [
      { h: `${fromU} can't be decoded`, p: what },
      { h: "There's no picture to convert", p: `Because nothing can read the ${fromU} container, there's no image data a converter can extract and re-save as ${toU}. Any tool that claims to do it would just fail.` },
      { h: 'Get the original photos instead', p: `The real photos an ${fromU} cache was built from live in your photo library or a backup. Export those (they'll be JPG or HEIC), then convert them — that's a conversion that works.` },
    ],
    alternatives: altCards(fromU, pool),
    faq: [
      [`Can any tool convert ${fromU} to ${toU}?`, `No. ${fromU} is ${srcCat === 'raster' ? 'a proprietary thumbnail-cache blob' : 'an undocumented format'} that no image library can decode, so there's nothing to convert. Export the original photos and convert those instead.`],
      [`How do I open an ${fromU} file?`, `${fromU} files aren't meant to be opened directly. Recover the original images from your device's photo library or an iTunes/Finder backup, then work with those.`],
      [`What can I convert to ${toU} instead?`, `Once you have the real photos (JPG or HEIC), they convert cleanly to ${list} — free, no sign-up, no watermark.`],
    ],
  };
  const seg = pairSeg(fromU, toU) === 'image/converter' ? 'image' : pairSeg(fromU, toU);
  const url = BASE + '/' + seg + '/' + slug + '/';
  const name = fromU + ' to ' + toU;
  const fr = FORMATS.find((r) => String(r[F.EXT]).toUpperCase() === fromU);
  const fromInfo = fr ? { ext: fr[F.EXT], full: fr[F.FULL], dev: fr[F.DEV], what: fr[F.WHAT], open: fr[F.OPEN] } : null;
  const formatBlocks = [fmtBlock(fromU), fmtBlock(toU)].filter(Boolean);
  return {
    slug, name,
    title: `Convert ${fromU} to ${toU}? The Honest Answer — and What Works | SharkFoto`,
    desc: `You can't convert ${fromU} to ${toU} — ${fromU} isn't a readable image format. Here's why, and the conversions that actually work instead.`,
    heroSub: `Trying to open a ${fromU} as ${toU}? Here's the honest answer — and how to get a real image.`,
    canonical: url, url, ogImage: OG, appName: 'SharkFoto ' + name,
    explainer: true, rawTarget: true,
    kind: 'unreadable-source', from: fromU, to: toU, fromSlug: fromU.toLowerCase(),
    verdictTone: copy.verdictTone, verdict: copy.verdict, lead: copy.lead,
    whyTitle: copy.whyTitle, reasons: copy.reasons,
    alternatives: copy.alternatives,
    targetFormats: null, targetFormatsTitle: null, targetFormatsSub: null,
    fromInfo, formatBlocks, faq: copy.faq,
    cloud: explainerCloud(fromU), cloudTitle: 'Popular free tools',
    crumb: [
      ['Home', 'https://sharkfoto.com/'],
      ['Free Tools', BASE + '/'],
      seg === 'video' ? ['Video', BASE + '/video']
        : seg === 'audio' ? ['Audio', BASE + '/audio']
        : ['Image', BASE + '/image'],
      [name, url],
    ],
  };
}

// ---- cross-category explainer -------------------------------------
// The two sides are fundamentally different kinds of files so no conversion
// exists (image↔audio, or image/media↔data-serialization like plist/json/xml).
// Better than a bare 404: it's searched-for, and we give an honest answer +
// point at what the user can actually do with their source file. Same payload
// shape as explainerSEO so RawTargetExplainer.vue renders it unchanged.
// reason: 'audio-cross' | 'data-cross'. Scoped set (not the full matrix) — see
// seo-data.impossibleReason; these pages are NOT mass-added to the sitemap.
export function crossCategorySEO(fromU, toU, slug, reason) {
  const srcCat = catOf(fromU);
  const pool = altPool(fromU, srcCat);
  const list = humanJoin(pool);
  let verdict, lead, whyTitle, reasons, faq, descWhy;
  if (reason === 'data-cross') {
    const dataU = catOf(fromU) === 'plist' ? fromU : toU; // plist/json/xml side
    const srcIsData = dataU === fromU;
    descWhy = 'one is a data format, not an image';
    verdict = `We can't convert ${fromU} to ${toU} — ${dataU} is a data/settings format, not an image. Here's what actually works.`;
    lead = `A ${dataU} file holds text data, not a picture — so there's nothing to convert between ${fromU} and ${toU}. Here's the honest answer and what to do instead.`;
    whyTitle = `Why ${fromU} to ${toU} isn't possible`;
    reasons = [
      { h: `${dataU} isn't an image`, p: `A .${dataU.toLowerCase()} file stores text data — settings, coordinates, lists — not pixels. There's no image inside to extract, and a picture can't be saved as a ${dataU} data file.` },
      { h: `A sprite sheet is a pair, not one file`, p: `In game development a sprite atlas is a .png image PLUS a .plist that lists each sprite's coordinates. If that's what you need, export both together from a texture packer like TexturePacker or Cocos2d — no single-file converter can build it.` },
      srcIsData
        ? { h: `Convert the data instead`, p: `To move the data itself between formats, ${dataU} converts cleanly to ${list}.` }
        : { h: `Do this with your ${fromU} instead`, p: `To convert the image, ${fromU} works great as ${list}.` },
    ];
    faq = [
      [`Can you convert ${fromU} to ${toU}?`, `No — ${dataU} is a data format, not an image, so there's nothing to convert. ${srcIsData ? `You can convert the ${dataU} data to ${list} instead.` : `You can convert your ${fromU} to ${list} instead.`}`],
      [`What is a ${dataU} file?`, `${dataU} is a text-based data format used for app settings and, with a matching image, sprite-sheet coordinates. It isn't a picture.`],
      [`I have a sprite sheet — how do I split it?`, `A sprite sheet is a .png + .plist pair. Use a texture packer (TexturePacker, Cocos2d) to pack or unpack them together; a single-file converter can't.`],
    ];
  } else {
    const audioU = catOf(fromU) === 'audio' ? fromU : toU;
    const srcIsAudio = audioU === fromU;
    const srcKind = srcIsAudio ? 'a sound recording' : 'a picture';
    const tgtKind = srcIsAudio ? 'a picture' : 'a sound recording';
    descWhy = 'images and audio are different kinds of files';
    verdict = `We can't convert ${fromU} to ${toU} — images and audio are completely different kinds of files. Here's what actually works.`;
    lead = `A ${fromU} is ${srcKind} and a ${toU} is ${tgtKind} — there's nothing to convert between them. Here's the honest answer and what you can actually do with your ${fromU}.`;
    whyTitle = `Why ${fromU} to ${toU} isn't possible`;
    reasons = [
      { h: `Different kinds of files`, p: `An image stores pixels; an audio file stores a sound waveform. Neither contains the other, so no tool can turn ${srcKind} into ${tgtKind}.` },
      srcIsAudio
        ? { h: `Want a picture of the sound?`, p: `Album art or a waveform image is a design task — create the image, then convert that. Your ${fromU} audio itself can't become a ${toU}.` }
        : { h: `Want to add sound to the image?`, p: `Making a video or slideshow with music is a video tool, not a format conversion. Your ${fromU} image itself can't become ${toU} audio.` },
      { h: `Do this with your ${fromU} instead`, p: `${fromU} converts cleanly to ${list}.` },
    ];
    faq = [
      [`Can any tool convert ${fromU} to ${toU}?`, `No. ${srcIsAudio ? 'Audio and images' : 'Images and audio'} are fundamentally different, so there's nothing to convert. You can convert your ${fromU} to ${list} instead.`],
      [`What did I probably want?`, srcIsAudio ? `Maybe a waveform/album-art image, or adding this audio to a video — those are separate tools, not a ${fromU}→${toU} conversion.` : `Maybe a video or slideshow with audio — use a video tool. A ${fromU} image can't hold sound.`],
      [`What can I convert my ${fromU} to?`, `${fromU} converts cleanly to ${list} — free, no sign-up, no watermark.`],
    ];
  }
  const seg = pairSeg(fromU, toU) === 'image/converter' ? 'image' : pairSeg(fromU, toU);
  const url = BASE + '/' + seg + '/' + slug + '/';
  const name = fromU + ' to ' + toU;
  const fr = FORMATS.find((r) => String(r[F.EXT]).toUpperCase() === fromU);
  const fromInfo = fr ? { ext: fr[F.EXT], full: fr[F.FULL], dev: fr[F.DEV], what: fr[F.WHAT], open: fr[F.OPEN] } : null;
  const formatBlocks = [fmtBlock(fromU), fmtBlock(toU)].filter(Boolean);
  return {
    slug, name,
    title: `Convert ${fromU} to ${toU}? The Honest Answer — and What Works | SharkFoto`,
    desc: `You can't convert ${fromU} to ${toU} — ${descWhy}. Here's why, and the conversions that actually work instead.`,
    heroSub: `Looking to turn a ${fromU} into ${toU}? Here's the honest answer — and the conversions that actually work.`,
    canonical: url, url, ogImage: OG, appName: 'SharkFoto ' + name,
    explainer: true, rawTarget: true,
    kind: 'cross-category', from: fromU, to: toU, fromSlug: fromU.toLowerCase(),
    verdictTone: 'no', verdict, lead, whyTitle, reasons,
    alternatives: altCards(fromU, pool),
    targetFormats: null, targetFormatsTitle: null, targetFormatsSub: null,
    fromInfo, formatBlocks, faq,
    cloud: explainerCloud(fromU), cloudTitle: 'Popular free tools',
    crumb: [
      ['Home', 'https://sharkfoto.com/'],
      ['Free Tools', BASE + '/'],
      seg === 'video' ? ['Video', BASE + '/video'] : seg === 'audio' ? ['Audio', BASE + '/audio'] : ['Image', BASE + '/image'],
      [name, url],
    ],
  };
}

// ---- assemble the full SEO payload --------------------------------
export function explainerSEO(fromU, toU, slug) {
  const cap = capOf(toU) || { cat: catOf(toU), readable: false, writable: false, rec: 'explainer', feasible: 'no' };
  const kind = explainerKind(fromU, toU, cap);
  const copy = explainerCopy(fromU, toU, kind, cap);
  // 安全网:剔除自指备选(源恰好等于某个硬编码目标 → "Convert PNG to PNG"),保留其余顺序
  copy.alternatives = (copy.alternatives || []).filter((a) => {
    const m = (a.to || '').match(/([a-z0-9]+)-to-([a-z0-9]+)\/?$/i);
    return !(m && m[1].toLowerCase() === m[2].toLowerCase());
  });
  // 类别段由 pairSeg 决定(镜像 pairCategory):video/audio 源或目标 → video/audio,否则 image。
  // explainer 页 URL 无 /converter/ 子段,故把 'image/converter' 收敛为 'image'(修视频/音频不可写配对错放 /image/,nuxt#44)。
  const seg = pairSeg(fromU, toU) === 'image/converter' ? 'image' : pairSeg(fromU, toU);
  const url = BASE + '/' + seg + '/' + slug + '/';
  const name = fromU + ' to ' + toU;

  // source-format science (works for any known X)
  const fr = FORMATS.find((r) => String(r[F.EXT]).toUpperCase() === fromU);
  const fromInfo = fr ? { ext: fr[F.EXT], full: fr[F.FULL], dev: fr[F.DEV], what: fr[F.WHAT], open: fr[F.OPEN] } : null;
  // 富格式详情(源 + 目标),与 converter 页同深度 —— explainer 页不能比 converter 页薄。
  const formatBlocks = [fmtBlock(fromU), fmtBlock(toU)].filter(Boolean);

  const titleTail = copy.verdictTone === 'soon'
    ? `Convert ${fromU} to ${toU} — What You Can Do Today | SharkFoto`
    : `Convert ${fromU} to ${toU}? The Honest Answer — and What Works | SharkFoto`;
  const desc = copy.verdictTone === 'soon'
    ? `${fromU} to ${toU} isn't available yet — here's what ${toU} is, why it takes longer, and the conversions that work right now on SharkFoto.`
    : `You can't simply convert ${fromU} to ${toU} — here's the honest reason why, and the conversions that actually work instead.`;
  // Hero 副标题:只做"提问式引导",不重复下方红色 verdict 的"做不到"、不带 no-sign-up 推销(那对 can't-do 页不合适)。
  const heroSub = copy.verdictTone === 'soon'
    ? `Looking to convert ${fromU} to ${toU}? Here's where it stands — and what works right now.`
    : `Looking to convert ${fromU} to ${toU}? Here's the honest answer — and what works instead.`;

  return {
    slug, name,
    title: titleTail,
    desc, heroSub,
    canonical: url, url, ogImage: OG, appName: 'SharkFoto ' + name,
    explainer: true, rawTarget: true, // rawTarget kept as back-compat flag for the dispatch
    kind, from: fromU, to: toU, fromSlug: fromU.toLowerCase(),
    verdictTone: copy.verdictTone, verdict: copy.verdict, lead: copy.lead,
    whyTitle: copy.whyTitle, reasons: copy.reasons,
    alternatives: copy.alternatives,
    targetFormats: copy.targetFormats || null,
    targetFormatsTitle: copy.targetFormatsTitle || null,
    targetFormatsSub: copy.targetFormatsSub || null,
    fromInfo, formatBlocks, faq: copy.faq,
    cloud: explainerCloud(fromU), cloudTitle: 'Popular free tools',
    crumb: [
      ['Home', 'https://sharkfoto.com/'],
      ['Free Tools', BASE + '/'],
      seg === 'video' ? ['Video', BASE + '/video']
        : seg === 'audio' ? ['Audio', BASE + '/audio']
        : ['Image', BASE + '/image'],
      [name, url],
    ],
  };
}
